import {
  Button,
  Checkbox,
  Container,
  Divider,
  Grid,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { useClipboard } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { Copy, X } from 'tabler-icons-react';
import { Roles } from '../../enums/roles-enum';
import { generateCode } from '../../helpers/code-generator';
import { removeEmptyPropertiesWithNested } from '../../helpers/filters.helpers';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import {
  AdminCreatePOSsSchema,
  MerchantCreatePOSsSchema,
  PartnerCreatePOSsSchema,
  ResellerCreatePOSsSchema,
} from '../../schemas/create-pos.schema';
import { ErrorResponse } from '../../type/error-response.types';
import { EntityFilterData, EntityIdFilterData } from '../../type/filter.types';
import { CreatePOSsRequest, CreatePOSsResponse } from '../../type/pos.types';
import SelectBranchFilter from '../filters/select-branch-filter.component';
import SelectMerchantFilter from '../filters/select-merchant-filter.component';
import SelectPartnerFilter from '../filters/select-partner-filter.component';
import SelectResellerFilter from '../filters/select-reseller-filter.component';

function CreateNewPos() {
  const { auth } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const [isLoading, setIsLoading] = useState(false);
  const activationCodeClipboard = useClipboard();
  const authPassClipboard = useClipboard();
  const secretKeyClipboard = useClipboard();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState<CreatePOSsResponse>();

  const filterSelectData = useForm<EntityFilterData>({
    initialValues: {
      partnersData: [],
      resellersData: [],
      merchantsData: [],
      branchesData: [],
    },
  });
  const filterValues = useForm<EntityIdFilterData>({
    initialValues: {
      partner: undefined,
      reseller: undefined,
      merchant: undefined,
      branch: undefined,
    },
  });

  const form = useForm<CreatePOSsRequest>({
    initialValues: {
      posName: 'POS ' + generateCode(4),
      partnerId: undefined,
      resellerId: undefined,
      merchantId: undefined,
      branchId: undefined,
    },
    validate: joiResolver(
      auth?.role === Roles.ADMINISTRATOR
        ? AdminCreatePOSsSchema
        : auth?.role === Roles.PARTNER
        ? PartnerCreatePOSsSchema
        : auth?.role === Roles.RESELLER
        ? ResellerCreatePOSsSchema
        : MerchantCreatePOSsSchema
    ),
  });

  useEffect(() => {
    form.setFieldValue('partnerId', filterValues.values.partner);
    form.setFieldValue('resellerId', filterValues.values.reseller);
    form.setFieldValue('merchantId', filterValues.values.merchant);
    form.setFieldValue('branchId', filterValues.values.branch);
  }, [filterValues.values]);

  const handleClear = () => {
    form.reset();
    filterValues.reset();
    setGeneratedCodes(undefined);
    setDisableSubmit(false);
  };

  const getDynamicFilter = () => {
    let filters: JSX.Element = <></>;

    if (auth?.role === Roles.ADMINISTRATOR) {
      filters = (
        <Grid.Col xs={12} sm={6}>
          <SelectPartnerFilter
            filterSelectData={filterSelectData}
            filterValues={filterValues}
            error={form.errors.partnerId}
          />
        </Grid.Col>
      );
    }

    if (auth?.role === Roles.PARTNER || auth?.role === Roles.ADMINISTRATOR) {
      filters = (
        <>
          {filters}
          <Grid.Col xs={12} sm={6}>
            <SelectResellerFilter
              filterSelectData={filterSelectData}
              filterValues={filterValues}
              isPartnerDependent={auth?.role === Roles.ADMINISTRATOR}
              error={form.errors.resellerId}
            />
          </Grid.Col>
        </>
      );
    }

    if (
      auth?.role === Roles.PARTNER ||
      auth?.role === Roles.RESELLER ||
      auth?.role === Roles.ADMINISTRATOR
    ) {
      filters = (
        <>
          {filters}
          <Grid.Col xs={12} sm={6}>
            <SelectMerchantFilter
              filterSelectData={filterSelectData}
              filterValues={filterValues}
              isResellerDependent={
                auth?.role === Roles.PARTNER ||
                auth?.role === Roles.ADMINISTRATOR
              }
              error={form.errors.merchantId}
            />
          </Grid.Col>
        </>
      );
    }
    if (
      auth?.role === Roles.PARTNER ||
      auth?.role === Roles.RESELLER ||
      auth?.role === Roles.ADMINISTRATOR ||
      auth?.role === Roles.MERCHANT
    ) {
      filters = (
        <>
          {filters}
          <Grid.Col xs={12} sm={6}>
            <SelectBranchFilter
              filterSelectData={filterSelectData}
              filterValues={filterValues}
              isMerchantDependent={
                auth?.role === Roles.PARTNER ||
                auth?.role === Roles.RESELLER ||
                auth?.role === Roles.ADMINISTRATOR
              }
              error={form.errors.branchId}
            />
          </Grid.Col>
        </>
      );
    }
    return <Grid>{filters}</Grid>;
  };

  const handleSubmit = async () => {
    const request: CreatePOSsRequest = {
      partnerId: form.values.partnerId,
      resellerId: form.values.resellerId,
      merchantId: form.values.merchantId,
      branchId: form.values.branchId,
      posName: form.values.posName,
    };

    removeEmptyPropertiesWithNested(request);

    try {
      setIsLoading(true);
      const response = await axiosPrivate.post('web/pos', request);
      setGeneratedCodes(response.data);
      setDisableSubmit(true);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-retrieve-partners-data',
        message:
          error?.error ||
          err.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ flex: 1, margin: 0 }}>
      <Title order={2} my='lg'>
        Create POS
      </Title>
      <Paper
        withBorder
        radius='md'
        shadow='lg'
        style={{
          flex: 1,
          height: '100%',
          padding: '20px 0',
        }}
      >
        <Container p='lg' fluid>
          {getDynamicFilter()}
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Grid>
              <Grid.Col xs={12} sm={6}>
                <Text size='sm'>Change the POSs to your desired names.</Text>
                <TextInput
                  placeholder='POS name'
                  label='POS Name'
                  {...form.getInputProps('posName')}
                />
              </Grid.Col>
              {/* empty */}
              <Grid.Col p={0} xs={0} sm={6}></Grid.Col>
              <Grid.Col xs={12} sm={6}>
                <Stack>
                  <Checkbox label='Email Activation Code?' />
                </Stack>
              </Grid.Col>
              <Grid.Col>
                <Button>Create Multiple POS</Button>
              </Grid.Col>
            </Grid>
            <Divider my='lg' />
            {generatedCodes && (
              <>
                <Grid>
                  <Grid.Col xs={12} sm={6}>
                    <Group grow align='end' spacing={0}>
                      <PasswordInput
                        label='Activation code'
                        variant='filled'
                        value={generatedCodes.activationCode}
                        readOnly
                        rightSectionWidth={116}
                        mt='md'
                      />
                      <div>
                        <Button
                          size='md'
                          color={
                            activationCodeClipboard.copied ? 'green' : 'gray'
                          }
                          rightIcon={<Copy size={20} />}
                          onClick={() =>
                            activationCodeClipboard.copy(
                              generatedCodes.activationCode
                            )
                          }
                        >
                          {activationCodeClipboard.copied ? 'Copied' : 'Copy'}
                        </Button>
                      </div>
                    </Group>
                  </Grid.Col>
                  <Grid.Col xs={12} sm={6}>
                    <Group grow align='end' spacing={0}>
                      <PasswordInput
                        label='Auth pass'
                        variant='filled'
                        value={generatedCodes.authPass}
                        readOnly
                        mt='md'
                        width='100%'
                      />
                      <div>
                        <Button
                          size='md'
                          color={authPassClipboard.copied ? 'green' : 'gray'}
                          rightIcon={<Copy size={20} />}
                          onClick={() =>
                            authPassClipboard.copy(generatedCodes.authPass)
                          }
                        >
                          {authPassClipboard.copied ? 'Copied' : 'Copy'}
                        </Button>
                      </div>
                    </Group>
                  </Grid.Col>
                  <Grid.Col xs={12}>
                    <Group grow align='end' spacing={0}>
                      <PasswordInput
                        label='Secret key'
                        variant='filled'
                        value={generatedCodes.secretKey}
                        readOnly
                        rightSectionWidth={116}
                        mt='md'
                      />
                      <div>
                        <Button
                          size='md'
                          color={secretKeyClipboard.copied ? 'green' : 'gray'}
                          rightIcon={<Copy size={20} />}
                          onClick={() =>
                            secretKeyClipboard.copy(generatedCodes.secretKey)
                          }
                        >
                          {secretKeyClipboard.copied ? 'Copied' : 'Copy'}
                        </Button>
                      </div>
                    </Group>
                  </Grid.Col>
                </Grid>
                <Divider my='lg' />
              </>
            )}

            <Group position='apart'>
              <Button color='gray' onClick={handleClear}>
                Reset form
              </Button>
              <Button
                type='submit'
                loading={isLoading}
                disabled={disableSubmit}
              >
                Submit
              </Button>
            </Group>
          </form>
        </Container>
      </Paper>
    </div>
  );
}
export default CreateNewPos;
